import { useEffect, useRef, useState } from "react";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { auth } from "../../firebase";
import companiesQuery from "../../firestore/Companies.query";
import { newOnlineUrls, OnlineUrls } from "../../model/OnlineUrls";
import { OnlineUrlSettingDeleteModal } from "./OnlineUrlSettingDeleteModal";
import { OnlineUrlSettingModal } from "./OnlineUrlSettingModal";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import authApi from "../../api/Auth.api";
import { UserRecord } from "firebase-admin/auth";
import { ConstMessage, CreateUserName, CreateUserType } from "../../config/const";

export const OnlineUrlSettingPage = () => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();

  const isDisabled = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [onlineUrlsList, setOnlineUrlsList] = useState<OnlineUrls[]>([]);
  const [onlineUrl, setOnlineUrl] = useState<OnlineUrls>(newOnlineUrls);
  const [deleteOnlineUrl, setDeleteOnlineUrl] = useState<OnlineUrls | undefined>();
  const [staffList, setStaffList] = useState<UserRecord[]>([]);

  useEffect(() => {
    const f = async () => {
      try {
        // 返信ボタン一覧取得
        const onlineUrlsList = await companiesQuery.getOnlineUrls(currentUser?.tenantId!);
        setOnlineUrlsList(onlineUrlsList);

        // 返信ボタンデータ登録ユーザの一覧を作成（重複削除）
        const createIdArr = Array.from(
          new Map(onlineUrlsList.map((item) => [item.createUser, item.createUser])).values()
        );

        let staffList = [];
        for(let i = 0; i < createIdArr.length; i++) {
          // データ登録ユーザの情報を取得
          const result = await authApi.requestGetStaffInfo(currentUser?.tenantId!, createIdArr[i]);
          if (result.success && result.data !== undefined) {
            let userRec = result.data;
            userRec.uid = createIdArr[i];
            // uidの情報を付加し一覧に格納
            staffList.push(userRec as UserRecord);

          } else if (!result.success) {
            throw new Error(result.data);
          }
        };
        setStaffList(staffList);

      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));

      } finally {
        setIsLoad(false);
      }
    };
    f();
  }, [currentUser?.tenantId, isOpen, dialogIsOpen]);

  const columns: GridCol[] = [
    {
      field: "number",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "name",
      headerName: "名称",
      style: "",
    },
    {
      field: "type",
      headerName: "作成者",
      style: "",
    },
    {
      field: "control",
      headerName: "編集/削除",
      style: "w-2/12",
    },
  ];

  /**
   * 作成者を返却する
   * 
   * @param target 
   * @returns 運営で作成：運営 / 団体で作成：作成者名（削除済の管理者の場合：団体）
   */
  const getRegisterName = (target: OnlineUrls) => {
    if (target.type === CreateUserType.ADMIN) {
      return CreateUserName.ADMIN;
    } else {
      const registerUser = staffList.filter((item: UserRecord) => item.uid == target.createUser);
      if (0 < registerUser.length && registerUser[0].displayName !== undefined) {
        return registerUser[0].displayName;
      } else {
        // 団体管理者が取得できなかった場合
        return CreateUserName.MUNICIPALITY;
      }
    }
  };

  const rowList: GridRow[] = onlineUrlsList.map((onlineUrls, index) => {
    return {
      rowData: {
        number: index + 1,
        name: (<div className={"sm:whitespace-normal"}>{onlineUrls.name ?? ''}</div>),
        type: getRegisterName(onlineUrls),
        control: onlineUrls.type !== "admin" ? (
          <div className="flex space-x-2">
            <Button
              onClick={() => {
                setIsUpdate(true);
                setOnlineUrl(onlineUrls);
                toggle();
              }}
              className={"whitespace-nowrap"}
              children={"編集"}/>
            <Button
              color="red"
              onClick={() => {
                deleteDialogToggle();
                setDeleteOnlineUrl(onlineUrls);
              }}
              className={"whitespace-nowrap"}
              children={"削除"}/>
          </div>
        ) : ''
      },
      sortableRowData: {
        name: onlineUrls.name ?? '',
        type: onlineUrls.type,
        control: onlineUrls.type
      }
    };
  });

  const onSubmit = async (data: OnlineUrls) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        const value: OnlineUrls = {
          id: onlineUrl?.id ?? '',
          onlineType: "Zoom",
          name: data.name,
          url: data.url,
          type: "municipality",
          createUser: currentUser?.uid!,
          createAt: onlineUrl?.createAt ?? new Date(),
        }
        // ミーティングURL取得
        let onlineUrls =await companiesQuery.getOnlineUrls(currentUser?.tenantId!);
        // 編集対象のミーティングURL以外のミーティングURLを取得
        onlineUrls = onlineUrls.filter(onlineUrl => onlineUrl.id !== data.id)
        // ミーティングURL名称が一致するミーティングURLを取得し重複チェックを行う
        onlineUrls = onlineUrls.filter(onlineUrl => onlineUrl.name === data.name)
        if (onlineUrls.length > 0){
          // 重複した場合、dupCheckをtrueにする
          sessionStorage.setItem("dupCheck", "true");
        }
        // 重複しない場合、 ミーティングURL保存処理実行
        if(sessionStorage.getItem("dupCheck") === "false"){
          if(isUpdate) {
            await companiesQuery.updateOnlineUrl(currentUser?.tenantId!, value);
          } else {
            data.createUser = currentUser?.uid!;
            data.createAt = new Date();
            await companiesQuery.setOnlineUrl(currentUser?.tenantId!, value);
          }
          dispatch(alertOpen(AlertValue("URLを保存しました", "success")));
          toggle();
        }
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        console.error(err);
      } finally {
        isDisabled.current = false;
      }
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const deleteDialogToggle = () => {
    setDialogIsOpen(!dialogIsOpen);
  };

  const onDelete = (onlineUrlsId: string) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        deleteDialogToggle();
        companiesQuery.deleteOnlineUrl(currentUser?.tenantId!, onlineUrlsId);
        dispatch(alertOpen(AlertValue("削除しました。", "success")));
        isDisabled.current = false;
      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue("削除に失敗しました", "danger")));
        isDisabled.current = false;
      }
    }
  }

  return (
    <div className="h-full">
      <OnlineUrlSettingModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        onlineUrl={onlineUrl}
        authorUid={currentUser?.uid!}
        onSubmit={onSubmit}
      />
      <OnlineUrlSettingDeleteModal
        children={undefined}
        open={dialogIsOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        onlineUrl={deleteOnlineUrl}
        handle={onDelete}
      />
      <div className="sm:flex px-6 pt-6 pb-4 sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl font-semibold text-gray-900">
            ミーティングURL設定
          </h2>
        </div>
        <div>
          <Button
            size={"sm"}
            onClick={() => {
              setIsUpdate(false);
              setOnlineUrl(newOnlineUrls);
              toggle();
            }}
          >
            ミーティングURL新規登録
          </Button>
        </div>
      </div>
      {isLoad === true ? <Spiner /> : (
      <div className={"px-6"}>
        <TableList
          header
          gridCol={columns}
          gridRow={rowList}
          defaultSortOrder={"desc"}
          limit={50}
        />
      </div>
    )}
    </div>);
}